import React from "react";
import { useTranslation } from "react-i18next";

import { useTheme } from "@mui/material/styles";
import { highLatencyThresholdMs } from "@screenView/api/ScreenViewClient";
import { useScreenView } from "@screenView/hooks/useScreenView";
import { ConnectionStatus, ScreenViewClientConfig, StreamingMode } from "@screenView/types";

import { useStyles } from "./ScreenView.style";
import { getButtonBackgroundColor, getStreamingModeColor, getStreamingModeLabel } from "./ScreenView.utils";

// TODO: remove this once screen view is fully implemented
const showControlButtons = false;

const defaultWidth = 1920;
const defaultHeight = 1080;

export const ScreenView: React.FC<ScreenViewClientConfig> = (props) => {
    const { classes } = useStyles();
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common"]);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const {
        setCanvas,
        isConnected,
        status,
        error,
        streamingMode,
        currentRTT,
        sendMouseClick,
        sendMouseMove,
        sendMouseWheel,
        sendKey,
        retryH264,
        switchToH264,
        switchToScreenshots,
        switchToAdaptive,
    } = useScreenView(props);

    const transformCoordinates = (clientX: number, clientY: number): { x: number; y: number } | null => {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) {
            return null;
        }

        const normalizedX = (clientX - rect.left) / rect.width;
        const normalizedY = (clientY - rect.top) / rect.height;

        const width = canvasRef.current?.width ?? defaultWidth;
        const height = canvasRef.current?.height ?? defaultHeight;

        const x = normalizedX * width;
        const y = normalizedY * height;

        return { x, y };
    };

    const handleMousePress = (e: React.MouseEvent<HTMLCanvasElement>, isDown: number) => {
        e.preventDefault();

        canvasRef.current?.focus();

        const coords = transformCoordinates(e.clientX, e.clientY);
        if (!coords) {
            return;
        }

        sendMouseClick(coords.x, coords.y, e.button, isDown);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (e.buttons === 0) {
            return;
        }

        const coords = transformCoordinates(e.clientX, e.clientY);
        if (!coords) {
            return;
        }

        sendMouseMove(coords.x, coords.y);
    };

    const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
        e.preventDefault();
        sendMouseWheel(e.deltaX, e.deltaY);
    };

    const handleKeyboardPress = (e: React.KeyboardEvent<HTMLCanvasElement>, isDown: number) => {
        e.preventDefault();

        sendKey(e.code, isDown, {
            ctrl: e.ctrlKey ? 1 : 0,
            alt: e.altKey ? 1 : 0,
            shift: e.shiftKey ? 1 : 0,
            meta: e.metaKey ? 1 : 0,
        });
    };

    const handleContextMenu = (e: React.MouseEvent<HTMLCanvasElement>) => {
        e.preventDefault();
    };

    React.useEffect(() => {
        if (canvasRef.current) {
            setCanvas(canvasRef.current);
        }
    }, [setCanvas]);

    React.useEffect(() => {
        if (!isConnected) {
            return;
        }

        const handleDocumentKeyPress = (e: KeyboardEvent, isDown: number) => {
            const target = e.target as HTMLElement;
            if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
                return;
            }

            if (target.tagName === "BUTTON") {
                return;
            }

            e.preventDefault();

            sendKey(e.code, isDown, {
                ctrl: e.ctrlKey ? 1 : 0,
                alt: e.altKey ? 1 : 0,
                shift: e.shiftKey ? 1 : 0,
                meta: e.metaKey ? 1 : 0,
            });
        };

        const handleKeyDown = (e: KeyboardEvent) => handleDocumentKeyPress(e, 1);
        const handleKeyUp = (e: KeyboardEvent) => handleDocumentKeyPress(e, 0);

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("keyup", handleKeyUp);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("keyup", handleKeyUp);
        };
    }, [isConnected, sendKey]);

    return (
        <div ref={containerRef} className={classes.container}>
            <div className={classes.statusOverlay}>
                {error ? <div className={classes.statusError}>{t("pages:screenViewPage.error", { error })}</div> : null}
                {!isConnected && !error ? (
                    <div className={classes.statusWarning}>
                        {status === ConnectionStatus.Connecting ? t("common:status.connecting") : t("common:status.disconnected")}
                    </div>
                ) : null}
                {isConnected ? (
                    <React.Fragment>
                        <div className={classes.statusSuccess}>{t("common:status.connected")}</div>
                        <div
                            className={classes.statusIndicator}
                            style={{
                                backgroundColor: getStreamingModeColor(streamingMode, currentRTT, theme),
                            }}
                            title={t("pages:screenViewPage.modeRTT", {
                                mode: getStreamingModeLabel(streamingMode),
                                rtt: currentRTT.toFixed(0),
                            })}
                        >
                            <span style={{ marginRight: "8px" }}>{getStreamingModeLabel(streamingMode)}</span>
                            <span>{currentRTT.toFixed(0) + "ms"}</span>
                        </div>
                        {streamingMode === StreamingMode.Adaptive && currentRTT > highLatencyThresholdMs && (
                            <button
                                onClick={retryH264}
                                className={classes.retryButton}
                                title={t("pages:screenViewPage.retryH264")}
                            >
                                {t("pages:screenViewPage.retryH264")}
                            </button>
                        )}
                    </React.Fragment>
                ) : null}
            </div>
            {isConnected && showControlButtons ? (
                <div className={classes.modeButtonsOverlay}>
                    <button
                        title={t("pages:screenViewPage.switchToH264Mode")}
                        className={classes.modeButton}
                        onClick={switchToH264}
                        style={getButtonBackgroundColor(StreamingMode.H264, theme)}
                    >
                        {t("pages:screenViewPage.mode.h264")}
                    </button>

                    <button
                        title={t("pages:screenViewPage.switchToScreenshotMode")}
                        className={classes.modeButton}
                        onClick={switchToScreenshots}
                        style={getButtonBackgroundColor(StreamingMode.Screenshot, theme)}
                    >
                        {t("pages:screenViewPage.mode.screenshot")}
                    </button>

                    <button
                        title={t("pages:screenViewPage.switchToAdaptiveMode")}
                        className={classes.modeButton}
                        onClick={switchToAdaptive}
                        style={getButtonBackgroundColor(StreamingMode.Adaptive, theme)}
                    >
                        {t("pages:screenViewPage.mode.adaptive")}
                    </button>
                </div>
            ) : null}

            <canvas
                tabIndex={0}
                width={defaultWidth}
                height={defaultHeight}
                ref={canvasRef}
                className={classes.canvas}
                onWheel={handleWheel}
                onMouseMove={handleMouseMove}
                onContextMenu={handleContextMenu}
                onMouseDown={(e) => handleMousePress(e, 1)}
                onMouseUp={(e) => handleMousePress(e, 0)}
                onKeyUp={(e) => handleKeyboardPress(e, 0)}
                onKeyDown={(e) => handleKeyboardPress(e, 1)}
            />
        </div>
    );
};
